import { ContainerOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Popconfirm, Row, Space, Table, Tag, message } from "antd";
import { toTitleCase } from "common/utils";
import { useFetchRolesQuery } from "features/userManagementTable/userManagementAPI";
import {
  useArchiveCommunicationMethodMutation,
  useFetchCommunicationMethodsQuery,
} from "features/communicationMethodsTable/communicationMethodsAPI";
import CommunicationMethodModal from "features/communicationMethodsTable/components/communicationMethodModal";
import { useRef, useState } from "react";
import { getColumnSearchProps } from "common/antd-utils";
import styled from "styled-components";

const StyledTable = styled(Table)`
  margin-top: 1em;
  cursor: pointer;
`;

export default function SequencesTable() {
  const [isModalVisible, setModalVisible] = useState(false);
  const [selectedCommunicationMethod, setSelectedCommunicationMethod] = useState(null);
  const [archiveCommunicationMethod] = useArchiveCommunicationMethodMutation();
  const { data: communicationMethods, isLoading } = useFetchCommunicationMethodsQuery();
  const { data: roles } = useFetchRolesQuery({ userType: "agency" });

  const [filteredInfo, setFilteredInfo] = useState({});
  const searchInput = useRef(null);
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
  };
  const handleReset = (clearFilters) => {
    clearFilters();
  };
  const handleChange = (_, filters) => {
    setFilteredInfo(filters);
  };

  const handleArchiveCommunicationMethod = async ({ communicationMethodId }) => {
    const result = await archiveCommunicationMethod({ communicationMethodId });

    if ("data" in result) {
      message.success("Communication method archived successfully!");
    }
  };

  const mediumTitle = (record) => {
    const title = toTitleCase({ str: record.medium, delimiter: "_" }) ?? "-";
    return record.useMultipleAccountsPerTask ? `${title} (Multiple Accounts)` : title;
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      ...getColumnSearchProps("name", searchInput, handleSearch, handleReset),
      // @ts-ignore
      filteredValue: filteredInfo?.name ?? null,
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Letter Code",
      dataIndex: "letterCode",
      key: "letterCode",
      render: (index, record) => record.letterCode ?? "-",
    },
    {
      title: "Medium",
      dataIndex: "medium",
      key: "medium",
      render: (index, record) => mediumTitle(record),
      ...getColumnSearchProps("medium", searchInput, handleSearch, handleReset),
      // @ts-ignore
      filteredValue: filteredInfo?.medium ?? null,
      sorter: (a, b) => a.medium.localeCompare(b.medium),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Provider",
      dataIndex: "provider",
      key: "provider",
      render: (index, record) => toTitleCase({ str: record.provider, delimiter: "_" }) ?? "-",
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
    },
    {
      title: "Allowed Groups",
      dataIndex: "allowedGroupIds",
      key: "allowedGroupIds",
      render: (_, record) => {
        const allowedGroups = record.allowedGroupIds ?? [];
        if (allowedGroups.length === 0) {
          return <Tag color="green">All Roles</Tag>;
        }
        return allowedGroups.map((groupId) => {
          const role = roles?.find((r) => r.id === groupId);
          return (
            <Tag key={groupId} color="blue">
              {role?.name ?? groupId}
            </Tag>
          );
        });
      },
    },
    {
      title: "Actions",
      fixed: "right",
      dataIndex: "action",
      key: "action",
      render: (_, record) =>
        !record.isArchived && (
          <Space size="middle">
            <EditOutlined
              key="edit"
              disabled={record.isArchived}
              onClick={() => {
                setSelectedCommunicationMethod(record);
                setModalVisible(true);
              }}
            />
            <Popconfirm
              placement="topLeft"
              okText="Yes"
              title="Are you sure you want to archive this communication method?"
              description={
                <p>
                  Archiving the communication method will archive all the future scheduled
                  communications (e.g text, email) associated with this communication method. <br />
                  You must delete communication steps using this communication method before taking
                  this action.
                </p>
              }
              onConfirm={() =>
                handleArchiveCommunicationMethod({ communicationMethodId: record.id })
              }
            >
              <ContainerOutlined key="archive" />
            </Popconfirm>
          </Space>
        ),
    },
  ];

  const onNewRuleOk = () => {
    setModalVisible(false);
    setSelectedCommunicationMethod(null);
  };

  return (
    <>
      <Row align="middle">
        <Button onClick={() => setModalVisible(true)} type="link" icon={<PlusOutlined />}>
          Add Communication Method
        </Button>
      </Row>
      <StyledTable
        loading={isLoading}
        bordered
        scroll={{ x: "max-content" }}
        // @ts-ignore
        columns={columns}
        dataSource={communicationMethods?.filter((method) => !method.isArchived)}
        onChange={handleChange}
      />
      {isModalVisible && (
        <CommunicationMethodModal
          title={`${selectedCommunicationMethod ? "Edit" : "Add"} Communication Method`}
          open={isModalVisible}
          onOk={onNewRuleOk}
          selectedCommunicationMethod={selectedCommunicationMethod}
          onCancel={() => {
            setModalVisible(false);
            setSelectedCommunicationMethod(null);
          }}
        />
      )}
    </>
  );
}
